import { useCallback, useEffect, useRef, useState } from "react";
import { useAppContextNonNull } from "@contexts/AppContext";
import ScreenOptionsEnum from "@models/ScreenOptionsEnum";
import ITokenSettings from "@models/ITokenSettings";
import ISupplierSettings from "@models/ISupplierSettings";
import EndpointsEnum from "@models/EndpointsEnum";
import UserTypesEnum from "@models/UserTypesEnum";
import IUpdateTokenSettingsDTO from "@models/DTO/IUpdateTokenSettingsDTO";
import IUpdateSupplierSettingsDTO from "@models/DTO/IUpdateSupplierSettingsDTO";
import ILoadUserDTO from "@models/DTO/ILoadUserDTO";
import { IScreenFilterPresets } from "@models/IFilterPresets";
import { IFilterPresetRuntime } from "@models/IFilterPresetsUI";
import UserService from "@services/UserService";
import { mergePresets, stripRuntimePreset } from "@utils/filterPresets";
import { migrateFilterPresets } from "@utils/filterPresetMigration";

const RELOAD_TIMEOUT_MS = 10000;

/**
 * Loads, migrates, merges and persists FilterBar presets for one screen.
 *
 * - Presets are read from the current token's settings (user) and the supplier settings (team).
 * - On first use, legacy `<screen>_filters` are migrated into `filterPresets` and persisted.
 * - Writes go through the existing `TokenUpdateSettings` / `SupplierUpdateSupplierSettings` socket
 *   events with the COMPLETE `filterPresets` object (the API replaces that key whole). Runtime
 *   fields are stripped first; the API rejects the whole write otherwise and emits an error notify.
 * - After a token write succeeds, the user model is reloaded so the next mount sees the new presets.
 *
 * Returns `presets: null` until the first merge so a default preset can auto-load on mount.
 */
export function useFilterPresets(screenKey: ScreenOptionsEnum) {
    const { userModel, supplierModel, socket } = useAppContextNonNull();
    const currentToken = UserService.getCurrentToken(userModel);
    const tokenSettings: ITokenSettings | undefined = currentToken?.settings || undefined;
    const supplierSettings: ISupplierSettings | undefined = supplierModel.settings;
    const isAdmin = !!userModel.isSupplierUser && userModel.userType?.id === UserTypesEnum.ADMIN;

    const [presets, setPresets] = useState<IFilterPresetRuntime[] | null>(null);
    const migrationAttempted = useRef(false);

    /** Reload the user model once the token settings write is acknowledged. */
    const reloadUserAfterTokenWrite = useCallback(() => {
        const timeout = setTimeout(() => socket.off(EndpointsEnum.TokenOnUpdateSettings, handler), RELOAD_TIMEOUT_MS);

        const handler = () => {
            clearTimeout(timeout);
            const payload: ILoadUserDTO = { userGuid: userModel.guid, userToken: userModel.token };
            socket.emit(EndpointsEnum.AppLoadUser, payload);
        };

        socket.once(EndpointsEnum.TokenOnUpdateSettings, handler);
    }, [socket, userModel]);

    const persistUserPresets = useCallback(
        (filterPresets: IScreenFilterPresets) => {
            if (!currentToken) {
                return;
            }

            const payload: IUpdateTokenSettingsDTO = {
                userGuid: userModel.guid,
                userToken: userModel.token,
                supplierGuid: supplierModel.guid,
                tokenGuid: currentToken.guid,
                settings: { filterPresets },
            };

            reloadUserAfterTokenWrite();
            socket.emit(EndpointsEnum.TokenUpdateSettings, payload);
        },
        [currentToken, userModel, supplierModel, socket, reloadUserAfterTokenWrite]
    );

    const persistTeamPresets = useCallback(
        (filterPresets: IScreenFilterPresets) => {
            const payload: IUpdateSupplierSettingsDTO = {
                userGuid: userModel.guid,
                userToken: userModel.token,
                supplierGuid: supplierModel.guid,
                settings: { filterPresets } as ISupplierSettings,
            };

            socket.emit(EndpointsEnum.SupplierUpdateSupplierSettings, payload);
        },
        [userModel, supplierModel, socket]
    );

    // ── Migrate (once, v2 mode only — this hook is only mounted by the FilterBar wrappers) then merge ──
    useEffect(() => {
        const settings: ITokenSettings = { ...(tokenSettings || {}) };

        if (!migrationAttempted.current && migrateFilterPresets(settings) && settings.filterPresets) {
            migrationAttempted.current = true;
            persistUserPresets(settings.filterPresets);
        }

        setPresets(mergePresets(supplierSettings, settings, screenKey, isAdmin));
    }, [tokenSettings, supplierSettings, screenKey, isAdmin, persistUserPresets]);

    // ── Save: split by source, strip runtime fields, send the complete filterPresets objects ──
    const onPresetsChange = useCallback(
        (updated: IFilterPresetRuntime[]) => {
            setPresets(updated);

            persistUserPresets({
                ...(tokenSettings?.filterPresets || {}),
                [screenKey]: updated.filter((p) => p.source === "user").map(stripRuntimePreset),
            });

            if (isAdmin) {
                persistTeamPresets({
                    ...(supplierSettings?.filterPresets || {}),
                    [screenKey]: updated.filter((p) => p.source === "team").map(stripRuntimePreset),
                });
            }
        },
        [persistUserPresets, persistTeamPresets, tokenSettings, supplierSettings, screenKey, isAdmin]
    );

    return { presets, onPresetsChange, isAdmin };
}
